<template>
  <div class="ticket-card-container">
    <div
      v-if="isLoading && !bookingDetails"
      class="ticket-card-container__loading"
    >
      {{ $t('myPage.bookings.loading') }}
    </div>
    <TicketCard
      v-else
      :bookingNumber="bookingNumber"
      :departureDate="departureDate"
      :departureTime="departureTime"
      :arrivalTime="arrivalTime"
      :originStation="originStation"
      :destinationStation="destinationStation"
      :segments="enrichedSegments"
      :isCancelled="isCancelled"
      :isProvisional="isProvisional"
      :hasDetails="!!bookingDetails"
      :isLoadingDetails="isLoading"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMyBookingsStore } from '@/stores/myBookingsStore';
import { searchServices } from '@/services/journeyService';
import TicketCard from './TicketCard.vue';

export default defineComponent({
  name: 'TicketCardContainer',
  components: {
    TicketCard,
  },
  props: {
    bookingNumber: {
      type: String,
      required: true,
    },
    bookingData: {
      type: Object,
      default: null,
    },
  },
  setup(props) {
    const { t } = useI18n();
    const myBookingsStore = useMyBookingsStore();
    const isLoading = ref(false);
    const serviceCache = ref<Record<string, any>>({}); // Cache for searchServices results

    // Define types for meta_data and booking summary
    type BookingMetaData = {
      cancel_datetime?: string;
      origin_name?: string;
      destination_name?: string;
      [key: string]: any;
    };

    type BookingSummary = {
      booking_number: string;
      meta_data?: BookingMetaData;
      segments?: any[];
      [key: string]: any;
    };

    type Segment = {
      direction: 'OUTBOUND' | 'INBOUND';
      service_name: string;
      travel_date: string;
      departure_station: {
        name: string;
        departure_platform?: string | null;
      };
      arrival_station: {
        name: string;
        arrival_platform?: string | null;
      };
      booking_journey_segments?: Array<{
        service_name: string;
        departure_station: {
          name: string;
          departure_platform?: string | null;
        };
        arrival_station: {
          name: string;
          arrival_platform?: string | null;
        };
      }>;
      meta_data?: Array<{
        key: string;
        value: string;
      }>;
    };

    const bookingSummary = computed<BookingSummary | undefined>(() => {
      return (
        myBookingsStore.futureBookings.find(
          (b: BookingSummary) => b.booking_number === props.bookingNumber
        ) ||
        myBookingsStore.pastBookings.find(
          (b: BookingSummary) => b.booking_number === props.bookingNumber
        )
      );
    });

    const bookingDetails = computed(() => {
      return myBookingsStore.getBookingDetails(props.bookingNumber);
    });

    // Enrich segments with platform data
    const enrichedSegments = computed<Segment[]>(() => {
      const segments = bookingDetails.value?.outbound_booking_tariff_segments || bookingSummary.value?.segments || [];
      return segments.map((segment: Segment) => {
        const cacheKey = `${segment.service_name}_${segment.travel_date.split('T')[0]}`;
        const serviceData = serviceCache.value[cacheKey]?.data?.services?.[0];
        return {
          ...segment,
          departure_station: {
            ...segment.departure_station,
            departure_platform: serviceData?.departure_station?.departure_platform || segment.departure_station.departure_platform || null,
          },
          arrival_station: {
            ...segment.arrival_station,
            arrival_platform: serviceData?.arrival_station?.arrival_platform || segment.arrival_station.arrival_platform || null,
          },
          booking_journey_segments: segment.booking_journey_segments?.map((journeySegment) => ({
            ...journeySegment,
            departure_station: {
              ...journeySegment.departure_station,
              departure_platform: serviceData?.departure_station?.departure_platform || journeySegment.departure_station.departure_platform || null,
            },
            arrival_station: {
              ...journeySegment.arrival_station,
              arrival_platform: serviceData?.arrival_station?.arrival_platform || journeySegment.arrival_station.arrival_platform || null,
            },
          })),
        };
      });
    });

    // Fetch platform data for segments
    const fetchPlatformData = async () => {
      const segments = bookingDetails.value?.outbound_booking_tariff_segments || bookingSummary.value?.segments || [];
      if (segments.length === 0) return;

      isLoading.value = true;
      try {
        for (const segment of segments) {
          if (segment.service_name && segment.travel_date) {
            const cacheKey = `${segment.service_name}_${segment.travel_date.split('T')[0]}`;
            if (!serviceCache.value[cacheKey]) {
              try {
                const serviceData = await searchServices(
                  segment.service_name,
                  segment.travel_date.split('T')[0]
                );
                serviceCache.value[cacheKey] = serviceData;
              } catch (error) {
                console.error(`Error fetching service data for ${cacheKey}:`, error);
              }
            }
          }
        }
        console.log('Enriched segments:', enrichedSegments.value);
      } catch (error) {
        console.error('Error fetching platform data:', error);
      } finally {
        isLoading.value = false;
      }
    };

    const formatScheduleDate = (date: string) => {
      if (!date) return '';
      const options: Intl.DateTimeFormatOptions = {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        timeZone: 'Europe/Oslo',
      };
      return new Date(date).toLocaleDateString('nb-NO', options) + ' ';
    };

    const formatTime = (timestamp: string) => {
      if (!timestamp) return '';
      if (timestamp.includes('T')) {
        const timeMatch = timestamp.match(/T(\d{2}:\d{2}):/);
        if (timeMatch) return timeMatch[1];
      }
      if (/^\d{2}:\d{2}$/.test(timestamp)) {
        return timestamp;
      }
      try {
        const date = new Date(timestamp);
        if (!isNaN(date.getTime())) {
          return date.toLocaleTimeString('nb-NO', {
            hour: '2-digit',
            minute: '2-digit',
            timeZone: 'Europe/Oslo',
          });
        }
      } catch (e) {}
      return '';
    };

    const getOutboundSegment = (booking: any) => {
      if (!booking || !booking.segments) {
        return null;
      }
      const outboundSegments = booking.segments.filter(
        (segment: any) => segment.direction === 'OUTBOUND'
      );
      if (outboundSegments.length === 0) return null;
      if (outboundSegments.length > 1) {
        const firstSegment = outboundSegments[0];
        const lastSegment = outboundSegments[outboundSegments.length - 1];
        return {
          ...firstSegment,
          origin_station_code: firstSegment.origin_station_code,
          destination_station_code: lastSegment.destination_station_code,
          stationDetails: {
            departure_station: firstSegment.stationDetails?.departure_station,
            arrival_station: lastSegment.stationDetails?.arrival_station,
          },
        };
      }
      return outboundSegments[0];
    };

    const isCancelled = computed(() => {
      if (props.bookingData?.status === 'CANCELLED') {
        return true;
      }
      if (bookingDetails.value?.data?.booking?.meta_data?.cancel_datetime) {
        return true;
      }
      return bookingSummary.value?.meta_data?.cancel_datetime ? true : false;
    });

    const isProvisional = computed(() => {
      if (bookingDetails.value?.data?.booking) {
        const booking = bookingDetails.value.data.booking;
        const hasProvisionalOutbound =
          booking.outbound_booking_tariff_segments?.some((segment) =>
            segment.required_products?.some(
              (product) => product.provisional === true && !product.cancelled
            )
          );
        const hasProvisionalInbound =
          booking.inbound_booking_tariff_segments?.some((segment) =>
            segment.required_products?.some(
              (product) => product.provisional === true && !product.cancelled
            )
          );
        return hasProvisionalOutbound || hasProvisionalInbound;
      }
      return false;
    });

    const departureDate = computed(() => {
      if (
        bookingDetails.value?.data?.booking?.outbound_booking_tariff_segments
      ) {
        const segments =
          bookingDetails.value.data.booking.outbound_booking_tariff_segments;
        if (
          segments.length > 0 &&
          segments[0].booking_journey_segments?.length > 0
        ) {
          const journeySegment = segments[0].booking_journey_segments[0];
          if (journeySegment.departure_date_time) {
            return formatScheduleDate(journeySegment.departure_date_time);
          }
        }
      }
      const outboundSegment = getOutboundSegment(bookingSummary.value);
      return formatScheduleDate(
        outboundSegment?.departure_date_time ||
          outboundSegment?.schedule_date ||
          ''
      );
    });

    const departureTime = computed(() => {
      if (bookingDetails.value) {
        if (
          bookingDetails.value.outbound_booking_tariff_segments?.length > 0
        ) {
          const segment =
            bookingDetails.value.outbound_booking_tariff_segments[0];
          if (segment.booking_journey_segments?.length > 0) {
            const journeySegment = segment.booking_journey_segments[0];
            if (journeySegment.departure_date_time) {
              return formatTime(journeySegment.departure_date_time);
            }
          }
        }
      }
      const outboundSegment = getOutboundSegment(bookingSummary.value);
      return formatTime(
        outboundSegment?.stationDetails?.departure_station
          ?.departure_timestamp || ''
      );
    });

    const getLastOutboundSegment = () => {
      if (
        bookingDetails.value?.outbound_booking_tariff_segments?.length > 0
      ) {
        const segments = bookingDetails.value.outbound_booking_tariff_segments;
        return segments[segments.length - 1];
      }
      return null;
    };

    const arrivalTime = computed(() => {
      if (bookingDetails.value) {
        if (
          bookingDetails.value.outbound_booking_tariff_segments?.length > 0
        ) {
          const segments =
            bookingDetails.value.outbound_booking_tariff_segments;
          const lastSegment = segments[segments.length - 1];
          if (lastSegment.booking_journey_segments?.length > 0) {
            const journeySegment =
              lastSegment.booking_journey_segments[
                lastSegment.booking_journey_segments.length - 1
              ];
            if (journeySegment.arrival_date_time) {
              return formatTime(journeySegment.arrival_date_time);
            }
          }
        }
      }
      const outboundSegment = getOutboundSegment(bookingSummary.value);
      return formatTime(
        outboundSegment?.stationDetails?.arrival_station?.arrival_timestamp ||
          ''
      );
    });

    const originStation = computed(() => {
      if (isCancelled.value) {
        if (bookingSummary.value?.meta_data) {
          if (bookingSummary.value.meta_data.origin_name) {
            return bookingSummary.value.meta_data.origin_name;
          }
        }
        const outboundSegment = getOutboundSegment(bookingSummary.value);
        if (outboundSegment?.stationDetails?.departure_station?.name) {
          return outboundSegment.stationDetails.departure_station.name;
        }
        if (outboundSegment?.origin_station_code) {
          return outboundSegment.origin_station_code;
        }
        return '';
      }
      if (bookingDetails.value) {
        if (
          bookingDetails.value.outbound_booking_tariff_segments?.length > 0
        ) {
          const segment =
            bookingDetails.value.outbound_booking_tariff_segments[0];
          if (segment.departure_station?.name) {
            return segment.departure_station.name;
          }
        }
      }
      const outboundSegment = getOutboundSegment(bookingSummary.value);
      return (
        outboundSegment?.stationDetails?.departure_station?.name ||
        outboundSegment?.origin_station_code ||
        ''
      );
    });

    const destinationStation = computed(() => {
      if (isCancelled.value) {
        if (bookingSummary.value?.meta_data?.destination_name) {
          return bookingSummary.value.meta_data.destination_name;
        }
        const outboundSegment = getOutboundSegment(bookingSummary.value);
        if (outboundSegment?.stationDetails?.arrival_station?.name) {
          return outboundSegment.stationDetails.arrival_station.name;
        }
        return outboundSegment?.destination_station_code || '';
      }
      if (bookingDetails.value) {
        if (
          bookingDetails.value.outbound_booking_tariff_segments?.length > 0
        ) {
          const segments =
            bookingDetails.value.outbound_booking_tariff_segments;
          const lastSegment = segments[segments.length - 1];
          if (lastSegment.arrival_station?.name) {
            return lastSegment.arrival_station.name;
          }
        }
      }
      const outboundSegment = getOutboundSegment(bookingSummary.value);
      return (
        outboundSegment?.stationDetails?.arrival_station?.name ||
        outboundSegment?.destination_station_code ||
        ''
      );
    });

    onMounted(async () => {
      if (!bookingDetails.value) {
        isLoading.value = true;
        try {
          await myBookingsStore.fetchBookingDetails(props.bookingNumber);
          await fetchPlatformData(); // Fetch platform data after booking details
        } catch (error) {
          console.error(
            `Error fetching details for booking ${props.bookingNumber}:`,
            error
          );
        } finally {
          isLoading.value = false;
        }
      } else {
        await fetchPlatformData(); // Fetch platform data if bookingDetails already exists
      }
    });

    return {
      bookingDetails,
      isLoading,
      departureDate,
      departureTime,
      arrivalTime,
      originStation,
      destinationStation,
      enrichedSegments, // Use enrichedSegments instead of segments
      isCancelled,
      isProvisional,
    };
  },
});
</script>

<style lang="scss" scoped>
.ticket-card-container {
  &__loading {
    text-align: center;
    padding: 1rem;
    background-color: $clr-white;
    border-radius: $border-radius;
    box-shadow: $box-shadow-card;
    margin-bottom: 1rem;
  }
}
</style>